import { FCX, useOutsideClick } from "@datassential/platform-ui-lib";
import { useMemo, useRef, useState } from "react";
import cn from "classnames";
import './HeatScore.scss';
import { IFoodProfileConsumerData } from "@models/FoodProfileConsumer";

const FoodHeatScore: FCX<{
    keywordName: string;
    data: IFoodProfileConsumerData['heatScore'];
}> = ({
    keywordName,
    data,
}) => {
    const [isInfoShown, setIsInfoShown] = useState(false);
    const containerRef = useRef(null);

    const {
        triedIt,
        knowIt,
        loveIt,
        heatLevelId,
        config,
        tooltipText,
    } = data;

    const heatScoreItem = useMemo(
        () => config.find(item => item.id === heatLevelId),
        [config, heatLevelId]
    );

    useOutsideClick({
        ref: containerRef,
        callback: () => setIsInfoShown(false),
    });

    return (
        <>
            <div
                className="FoodHeatScore"
                ref={containerRef}
            >
                <div className="FoodHeatScore__content">
                    <div className="FoodHeatScore__row">
                        <div data-testid="textFoodConsumerHeatScoreValueLabel">
                            consumer familiarity with<b> {keywordName} </b>indicates that it is:
                        </div>
                        <div className="FoodHeatScore__row-content">
                            <div
                                className="FoodHeatScore__big-value FoodSectionConsumer__blur"
                                data-testid="textFoodConsumerHeatScoreValue"
                            >
                                {heatScoreItem?.name || 'No data'}
                            </div>
                        </div>
                    </div>
                    <div className="FoodHeatScore__row">
                        <div data-testid="textFoodConsumerHeatScoreChangeLabel">
                            change vs. 2 years ago:
                        </div>
                        <div className="FoodHeatScore__row-content">
                            <div className="FoodHeatScore__metric">
                                <div data-testid="textFoodConsumerHeatScoreKnowItLabel">
                                    know it
                                </div>
                                <div
                                    data-testid="textFoodConsumerHeatScoreKnowItValue"
                                    className={cn(
                                        "FoodHeatScore__value FoodSectionConsumer__blur",
                                        knowIt > 0 && 'is-bold',
                                    )}
                                >
                                    {knowIt > 0 ? '+' : ''}{knowIt.toFixed(1)}%
                                </div>
                            </div>
                            <div className="FoodHeatScore__metric">
                                <div data-testid="textFoodConsumerHeatScoreHaveTriedItLabel">
                                    tried it
                                </div>
                                <div
                                    data-testid="textFoodConsumerHeatScoreHaveTriedItValue"
                                    className={cn(
                                        "FoodHeatScore__value FoodSectionConsumer__blur",
                                        triedIt > 0 && 'is-bold',
                                    )}
                                >
                                    {triedIt > 0 ? '+' : ''}{triedIt.toFixed(1)}%
                                </div>
                            </div>
                            <div className="FoodHeatScore__metric">
                                <div data-testid="textFoodConsumerHeatScoreLoveItLabel">
                                    love it
                                </div>
                                <div
                                    data-testid="textFoodConsumerHeatScoreLoveItValue"
                                    className={cn(
                                        "FoodHeatScore__value FoodSectionConsumer__blur",
                                        loveIt > 0 && 'is-bold',
                                    )}
                                >
                                    {loveIt > 0 ? '+' : ''}{loveIt.toFixed(1)}%
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="FoodWidget__footer">
                    <div
                        className="FoodWidget__footer-link"
                        onClick={() => setIsInfoShown(!isInfoShown)}
                        data-testid="buttonFoodConsumerHeatScoreOpenInfo"
                    >
                        what's this
                    </div>
                </div>
            </div>
            <div
                className={cn("FoodWidget__overlay", !isInfoShown && "is-hidden")}
                style={{ paddingBottom: 8 }}
            >
                <div
                    className="FoodWidget__overlay-header is-extra-padding"
                    dangerouslySetInnerHTML={{ __html: tooltipText }}
                    data-testid="textFoodConsumerHeatScoreInfoHeader"
                />
                <div
                    className="FoodWidget__overlay-content is-extra-padding FoodWidget__overlay-cols"
                    data-testid="textFoodConsumerHeatScoreInfoContent"
                >
                    <div className="FoodWidget__overlay-col" style={{ alignItems: 'center' }}>
                        {config.map(({ id, name }) => (
                            <b key={id} className="FoodWidget__overlay-row">{name}</b>
                        ))}
                    </div>
                    <div className="FoodWidget__overlay-col">
                        {config.map(({ id, description }) => (
                            <div
                                key={id}
                                className="FoodWidget__overlay-row"
                                dangerouslySetInnerHTML={{ __html: description }}
                            />
                        ))}
                    </div>
                </div>
                <div className="FoodWidget__footer">
                    <div
                        className="FoodWidget__footer-link is-light"
                        data-testid="buttonFoodConsumerHeatScoreCloseInfo"
                    >
                        okay thanks
                    </div>
                </div>
            </div>
        </>
    );
};

export default FoodHeatScore;